// useSortableRows — the React port of window.initSortableTable (js/table-sort.js),
// built ONCE on the pure sortable-rows.ts core and reused by every sortable table
// (League Table, Squad, and Rankings in 04g-3). It replaces the legacy's in-place DOM
// reordering — which fought React's ownership of the rows, hence the earlier deferral —
// with: sort the data array (React re-renders the keyed rows in order) + a FLIP layout
// effect that slides them, played only on a sort interaction (matching the legacy, which
// animates on click/restore, not on a data refresh).
//
// Usage: rows + columns (define columns module-level so their identity is stable). The
// table spreads headerProps(i) onto each sortable header, rankProps onto the `#` header,
// puts containerRef on the .data-wrapper, and gives each row data-sort-key={stableKey}.

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  type SortColumn,
  type SortState,
  isColumnSortable,
  resolveAsc,
  sortByColumn,
} from "./sortable-rows.js";

const FLIP_MS = 350; // table-sort.js's 0.35s ease

export interface HeaderProps {
  className: string;
  onClick?: () => void;
}

export interface UseSortableRows<T> {
  rows: T[];
  sort: SortState | null;
  /** Props for a sortable column header at `colIndex` (adds `sortable` + the active
   *  `sort-asc`/`sort-desc` indicator; no-op for a non-sortable column). */
  headerProps: (colIndex: number) => HeaderProps;
  /** Props for the `#` rank header — click restores the original order. */
  rankProps: HeaderProps;
  /** Attach to the `.data-wrapper` so the FLIP effect can measure the rows. */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function useSortableRows<T>(
  sourceRows: readonly T[],
  columns: readonly SortColumn<T>[],
): UseSortableRows<T> {
  const [sort, setSort] = useState<SortState | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // A fresh data set (competition/season change) drops back to the default order, exactly
  // as the legacy re-inits on every refreshModuleBody. useLayoutEffect so it happens
  // before paint (no flash of new data under the old sort).
  const prevSource = useRef(sourceRows);
  useLayoutEffect(() => {
    if (prevSource.current !== sourceRows) {
      prevSource.current = sourceRows;
      setSort(null);
    }
  }, [sourceRows]);

  const rows = useMemo(
    () => (sort ? sortByColumn(sourceRows, columns, sort.colIndex, sort.asc) : sourceRows.slice()),
    [sourceRows, columns, sort],
  );

  // Only animate when a sort interaction just happened, not on a data change.
  const animateNext = useRef(false);
  const prevTops = useRef<Map<string, number>>(new Map());
  useLayoutEffect(() => {
    const cont = containerRef.current;
    if (!cont) return;
    const els = Array.from(cont.querySelectorAll<HTMLElement>(".data-row:not(.header)"));
    const tops = new Map<string, number>();
    els.forEach((el) => {
      const key = el.dataset.sortKey;
      if (key != null) tops.set(key, el.getBoundingClientRect().top);
    });
    if (animateNext.current) {
      els.forEach((el) => {
        const key = el.dataset.sortKey;
        if (key == null) return;
        const prev = prevTops.current.get(key);
        const next = tops.get(key);
        if (prev == null || next == null) return;
        const delta = prev - next;
        if (Math.abs(delta) < 1) return;
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
        el.getBoundingClientRect(); // force reflow so the next line animates
        el.style.transition = `transform ${FLIP_MS / 1000}s ease`;
        el.style.transform = "translateY(0)";
        el.addEventListener(
          "transitionend",
          () => {
            el.style.transition = "";
            el.style.transform = "";
          },
          { once: true },
        );
      });
      animateNext.current = false;
    }
    prevTops.current = tops;
  }, [rows]);

  const onHeaderClick = (colIndex: number) => {
    if (!isColumnSortable(columns[colIndex])) return;
    const asc = resolveAsc(columns, sourceRows, colIndex, sort);
    animateNext.current = true;
    setSort({ colIndex, asc });
  };

  const headerProps = (colIndex: number): HeaderProps => {
    if (!isColumnSortable(columns[colIndex])) return { className: "" };
    const active = sort?.colIndex === colIndex;
    const indicator = active ? (sort?.asc ? " sort-asc" : " sort-desc") : "";
    return { className: `sortable${indicator}`, onClick: () => onHeaderClick(colIndex) };
  };

  const rankProps: HeaderProps = {
    className: "",
    onClick: () => {
      animateNext.current = true;
      setSort(null);
    },
  };

  return { rows, sort, headerProps, rankProps, containerRef };
}
